import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Image, Eye } from 'lucide-react';

// Função utilitária para construir URL completa do backend
const getBackendUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

export interface FileAttachment {
  id: string;
  name: string;
  type: 'image' | 'document';
  category?: string;
  file: File | null; // Permitir null para arquivos existentes
  preview?: string;
  url?: string; // URL do arquivo no servidor
  size: number;
  mimetype?: string; // Preservar o mimetype original
}

interface FileUploadProps {
  files: FileAttachment[];
  onFilesChange: (files: FileAttachment[]) => void;
  maxFiles?: number;
  maxSizePerFile?: number; // em MB
  acceptedTypes?: string[];
  showCategories?: boolean;
  categories?: { value: string; label: string }[];
}

const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  maxFiles = 20,
  maxSizePerFile = 10,
  acceptedTypes = ['image/*', '.pdf', '.doc', '.docx', '.txt'],
  showCategories = true,
  categories = [
    { value: 'contrato', label: 'Contrato' },
    { value: 'escritura', label: 'Escritura' },
    { value: 'projeto_engenharia', label: 'Projeto de Engenharia' },
    { value: 'projeto_arquitetura', label: 'Projeto de Arquitetura' },
    { value: 'outros', label: 'Outros' },
  ],
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentCategories = categories;

  const handleFiles = (fileList: FileList) => {
    const validFiles: File[] = [];

    // Primeiro, validar todos os arquivos
    Array.from(fileList).forEach(file => {
      // Validação de tamanho
      if (file.size > maxSizePerFile * 1024 * 1024) {
        alert(
          `O arquivo "${file.name}" é muito grande. O tamanho máximo permitido é ${maxSizePerFile}MB.`
        );
        return;
      }

      // Validação de tipo
      const isValidType = acceptedTypes.some(type => {
        if (type.includes('*')) {
          // Para tipos como 'image/*', verificar se o tipo do arquivo começa com 'image/'
          const baseType = type.replace('*', '');
          return file.type.startsWith(baseType);
        } else if (type.startsWith('.')) {
          // Para extensões como '.pdf', verificar a extensão do arquivo
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        } else {
          // Para tipos MIME específicos como 'image/jpeg', verificar correspondência exata
          return file.type === type;
        }
      });

      if (!isValidType) {
        const supportedFormats = acceptedTypes
          .map(type => {
            if (type.includes('*')) {
              return type.replace('*', '') + '*';
            } else if (type.startsWith('.')) {
              return type.toUpperCase();
            } else {
              // Converter tipos MIME para formatos mais amigáveis
              const mimeToFormat: { [key: string]: string } = {
                'image/jpeg': 'JPEG',
                'image/jpg': 'JPG',
                'image/png': 'PNG',
                'image/svg+xml': 'SVG',
                'image/x-icon': 'ICO',
                'application/pdf': 'PDF',
              };
              return mimeToFormat[type] || type;
            }
          })
          .join(', ');

        alert(`O arquivo "${file.name}" não é suportado. Formatos aceitos: ${supportedFormats}`);
        return;
      }

      validFiles.push(file);
    });

    // Verificar limite de arquivos
    if (files.length + validFiles.length > maxFiles) {
      alert(
        `Você pode enviar no máximo ${maxFiles} arquivo${maxFiles > 1 ? 's' : ''}. Remova alguns arquivos antes de adicionar novos.`
      );
      return;
    }

    // Processar arquivos válidos
    const processFile = (file: File): Promise<FileAttachment> => {
      return new Promise(resolve => {
        const fileAttachment: FileAttachment = {
          id: Date.now() + Math.random().toString(),
          name: file.name,
          type: file.type.startsWith('image/') ? 'image' : 'document',
          file,
          size: file.size,
          category: file.type.startsWith('image/') ? undefined : 'outros',
          mimetype: file.type,
        };

        // Criar preview para imagens
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = e => {
            fileAttachment.preview = e.target?.result as string;
            resolve(fileAttachment);
          };
          reader.readAsDataURL(file);
        } else {
          resolve(fileAttachment);
        }
      });
    };

    // Processar todos os arquivos e aguardar os previews
    Promise.all(validFiles.map(processFile)).then(newFiles => {
      onFilesChange([...files, ...newFiles]);
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (fileId: string) => {
    onFilesChange(files.filter(f => f.id !== fileId));
  };

  const updateFileCategory = (fileId: string, category: string) => {
    onFilesChange(
      files.map(f =>
        f.id === fileId ? { ...f, category: category as FileAttachment['category'] } : f
      )
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    if (bytes === -1) return 'Arquivo existente'; // Para arquivos já salvos no servidor
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const openFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      {/* Área de Upload */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileInput}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFileInput();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Clique para selecionar arquivos ou arraste e solte aqui"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          className="hidden"
        />

        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-lg font-medium text-gray-900 mb-2">
          Arraste arquivos aqui ou clique para selecionar
        </p>
        <p className="text-sm text-gray-500">
          Máximo {maxFiles} arquivos, {maxSizePerFile}MB cada
        </p>
        <p className="text-xs text-gray-400 mt-1">Formatos: Imagens, PDF, DOC, DOCX, TXT</p>
      </div>

      {/* Lista de Arquivos */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">
            Arquivos Anexados ({files.length}/{maxFiles})
          </h4>

          {files.map(file => (
            <div key={file.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              {/* Preview pequeno para imagens */}
              <div className="flex-shrink-0">
                {file.type === 'image' && (file.preview || file.url) ? (
                  <div className="relative">
                    <img
                      src={
                        file.preview ||
                        (file.url && file.url.startsWith('/uploads/')
                          ? getBackendUrl(file.url)
                          : file.url)
                      }
                      alt={file.name}
                      className="h-12 w-12 object-cover rounded border border-gray-200"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 rounded flex items-center justify-center">
                      <Eye className="h-4 w-4 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ) : file.type === 'image' ? (
                  <Image className="h-12 w-12 text-green-500" />
                ) : (
                  <FileText className="h-12 w-12 text-primary-500" />
                )}
              </div>

              {/* Informações do arquivo */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              </div>

              {/* Categoria (apenas para documentos) */}
              {showCategories && file.type === 'document' && (
                <div className="flex-shrink-0">
                  <select
                    value={file.category || 'outros'}
                    onChange={e => updateFileCategory(file.id, e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    {documentCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Botões de ação */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => removeFile(file.id)}
                  className="p-1 text-red-400 hover:text-red-600"
                  title="Remover"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
